import { IRole } from "../user";

export type PermissionValueType =
  | UserPermissionType.EDITOR
  | UserPermissionType.VIEWER
  | UserPermissionType.NO_ACCESS
  | UserPermissionType.ADMIN;

export interface IUserManage {
  id: number | null;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  phone_number: string;
  investors: IUserManageInvestmentEntity[];
  // teams: string[];
  // groups: string[];
  // investment_entities: string[];
  note: string | null;
  role: IRole;
  invite_user: boolean | null;
  permissions?: Record<string, PermissionValueType>;
}

export interface IUserManageInvestmentEntity {
  id: number;
  name: string;
}

export interface IUserManagePermission {
  permission_type: string;
  access: PermissionValueType;
}

export interface IPermissionItem {
  key: string;
  title: string;
}

export interface IUserManageData {
  id?: number | null;
  first_name: string;
  last_name: string;
  note: string | null;
  phone_number: string;
  email: string;
  role_id: number;
  invite_user: boolean | null;
  permissions?: IUserManagePermission[];
  investor_ids?: number[];
}

// export interface IUpdateUserManage extends ICreateUserManage {
// }

export interface IUsersManageQueries {
  search?: string;
  role_id?: number[];
  group_id?: number[];
  per_page?: number;
  page?: number;
}

export interface InvestEntity {
  id: number;
  deal_name: string;
  parent_entities: string[];
  subordinated_entities: string[];
  users: Array<{
    profile_photo_url: string;
    role_id: number;
    account_id: number;
    full_name: string;
    id: number;
  }>;
}

// export interface IUserManage extends Omit<IUsersManage, "role_name"> {
//   role: IRole;
//   permissions: IUserManagePermission[];
// }

export interface IUserManageGroup {
  id: number;
  name: string;
}

export interface ManageTeam {
  id: number;
  team_name: string;
  users: Array<{
    id: number;
    profile_photo_url: string;
    role_id: number;
    account_id: number;
    full_name: string;
    email: string;
  }>;
  deals: Array<{
    id: number;
    deal_name: string;
    parent_deal: string;
    sub_deals: string[];
    other_teams: string[];
  }>;
  note: string;
}

export enum UserPermissionType {
  NO_ACCESS = "no_access",
  EDITOR = "editor",
  VIEWER = "viewer",
  ADMIN = "admin"
}

// ----- Contacts (backed by deal_general_info) -----

// A deal a contact is attached to (many-to-many pivot). Read-only in responses. Each link carries the
// contact's role on that deal — `deal_role` (key) + `deal_role_label` (display); both may be null.
export interface IContactDeal {
  id: number;
  name: string;
  deal_role: string | null;
  deal_role_label: string | null;
}

// A contact's deal-role option (GET /api/v0.0.3/contacts/deal-roles). Source the Role dropdown from
// this endpoint — do not hardcode the list.
export interface IContactDealRole {
  key: string;
  label: string;
}

// One deal link sent on create/patch: attach `deal_id` with an optional `deal_role` key (null = no role).
export interface IContactDealLink {
  deal_id: number;
  deal_role: string | null;
}

// Free-form JSON bag on a contact. The backend stores it verbatim; the FE reads
// `skill` / `asset_class` from it for the list view (they have no dedicated columns
// in the contract yet) and may attach anything else without a schema change.
export interface IContactAdditionalInformation {
  skill?: string | null;
  asset_class?: string | null;
  [key: string]: unknown;
}

// ContactResource — every single-record response and each list row returns this.
export interface IContact {
  id: number;
  // Legacy single-deal owner; null for standalone contacts. Read-only — the FE never sets it.
  deal_id: number | null;
  email: string | null;
  firm: string | null;
  first_name: string | null;
  last_name: string | null;
  title: string | null;
  phone_number: string | null;
  address: string | null;
  country: string | null;
  referral: string | null;
  additional_information: IContactAdditionalInformation | null;
  deals: IContactDeal[];
  created_at: string;
  updated_at: string;
}

// Query params for GET /contacts. `deal_id` is supported by the list endpoint; `firm` (Organization
// filter) is prepared — the list currently filters firm only via `search`, so the backend needs to add
// a dedicated `firm` param for it to take effect (kept optional; stripped when empty).
export interface IContactsQueries {
  search?: string;
  deal_id?: number;
  firm?: string;
  page?: number;
  per_page?: number;
}

// Writable body for POST/PATCH /contacts. All fields optional; PATCH is a true partial update
// (omitted fields keep their stored value). `deals` is a full sync of the deal links (with per-link role).
export interface IContactPayload {
  first_name?: string;
  last_name?: string;
  email?: string;
  firm?: string;
  title?: string;
  phone_number?: string;
  address?: string;
  country?: string;
  referral?: string;
  additional_information?: IContactAdditionalInformation | null;
  deals?: IContactDealLink[];
}

// Inner paginator of GET /contacts (the response is doubly nested: AltiusRes<AltiusPage<IContact[]>>).
export interface IContactsPage {
  data: IContact[];
  total: number;
  per_page: number;
  current_page: number;
}
